// Scene reel — the projection the backend adds to an Ad row (API contract §4,
// liquidretail_backend services/sceneReelProjection.js) and the helpers every
// ad surface reads it through.
//
// A scene reel is one 10 s video cut from four independently generated scenes
// (3 + 3 + 2 + 2 s): three lifestyle scenes and a closing product shot. Each
// product gets three Ads — 9:16 and 16:9 are generated, 1:1 is cropped from the
// 9:16 for free (role 'derive', masterAdId names the 9:16 row).
//
// INERT BY CONSTRUCTION. `sceneReel` is absent on every non-scene ad and on any
// backend that predates the workflow, and toSceneReelSummary(undefined) is
// null, so every caller that branches on it renders exactly what it rendered
// before. Never fabricate a summary from other fields.

export const SCENE_REEL_ROUTE = 'scene_reel';

/** Seconds per scene, in order. Only used when a payload omits `cut`. */
export const SCENE_REEL_DEFAULT_CUT: readonly number[] = [3, 3, 2, 2];

export type SceneReelSceneStatus = 'pending' | 'running' | 'ready' | 'failed';
export type SceneReelQcGrade = 'pass' | 'minor' | 'fail';
export type SceneReelSceneKind = 'lifestyle' | 'closing';

export type SceneReelSceneSummary = {
  /** 0-based scene position in the cut. Display as index + 1. */
  index:    number;
  kind:     SceneReelSceneKind;
  status:   SceneReelSceneStatus;
  thumbUrl: string | null;
  qc:       SceneReelQcGrade | null;
  takes:    number;
};

export type SceneReelSummary = {
  model:       string | null;
  aspect:      string | null;
  /** 'derive' is the free 1:1 crop; scenes are edited on its masterAdId. */
  role:        'master' | 'derive';
  masterAdId:  string | null;
  cut:         number[];
  assembledAt: string | null;
  /** null when no scene has failed. A failed scene holds the whole reel. */
  failedScene: { index: number; reason: string | null } | null;
  scenes:      SceneReelSceneSummary[];
  regenerable: boolean;
  /**
   * A retitle is stamped for adgen and has not settled yet (contract §4). A
   * scene regenerate settles (`regenerating` → false) when its plate lands
   * and stamps this retitle in the same write; only the retitle writes the
   * titled renderUrl and moves a failed reel to draft. False when absent.
   */
  retitlePending: boolean;
};

const SCENE_STATUSES: readonly SceneReelSceneStatus[] = ['pending', 'running', 'ready', 'failed'];
const QC_GRADES: readonly SceneReelQcGrade[] = ['pass', 'minor', 'fail'];

function str(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null;
}

function int(v: unknown): number | null {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : null;
}

function toScene(raw: unknown, fallbackIndex: number): SceneReelSceneSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  const status = SCENE_STATUSES.includes(s.status as SceneReelSceneStatus)
    ? s.status as SceneReelSceneStatus
    : 'pending';
  const qc = QC_GRADES.includes(s.qc as SceneReelQcGrade) ? s.qc as SceneReelQcGrade : null;
  return {
    index:    int(s.index) ?? fallbackIndex,
    kind:     s.kind === 'closing' ? 'closing' : 'lifestyle',
    status,
    thumbUrl: str(s.thumbUrl),
    qc,
    takes:    int(s.takes) ?? 0
  };
}

/**
 * Normalise a projected `sceneReel` block. Returns null for anything that is
 * not an object (absent field, older backend, non-scene ad). Idempotent, so
 * it is safe on payloads that are already ExpansionAd-shaped.
 */
export function toSceneReelSummary(raw: unknown): SceneReelSummary | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const cut = Array.isArray(r.cut) && r.cut.length && r.cut.every(n => typeof n === 'number' && n > 0)
    ? (r.cut as number[]).slice()
    : SCENE_REEL_DEFAULT_CUT.slice();
  const scenes = Array.isArray(r.scenes)
    ? r.scenes
        .map((s, i) => toScene(s, i))
        .filter((s): s is SceneReelSceneSummary => !!s)
        .sort((a, b) => a.index - b.index)
    : [];
  const failedRaw = r.failedScene && typeof r.failedScene === 'object'
    ? r.failedScene as Record<string, unknown>
    : null;
  const failedIndex = failedRaw ? int(failedRaw.index) : null;
  return {
    model:       str(r.model),
    aspect:      str(r.aspect),
    role:        r.role === 'derive' ? 'derive' : 'master',
    masterAdId:  str(r.masterAdId),
    cut,
    assembledAt: str(r.assembledAt),
    failedScene: failedRaw && failedIndex != null
      ? { index: failedIndex, reason: str(failedRaw.reason) }
      : null,
    scenes,
    regenerable: r.regenerable === true,
    retitlePending: r.retitlePending === true
  };
}

/** True for a scene-reel Ad. Either signal is enough; neither is ever guessed. */
export function isSceneReelAd(ad: { renderRoute?: string | null; sceneReel?: unknown }): boolean {
  return ad.renderRoute === SCENE_REEL_ROUTE || !!ad.sceneReel;
}

/**
 * Follow-up work still in flight on a scene reel row: a scene regenerate, or
 * the retitle it stamped when it settled (contract §6 "Following a press to
 * the end"). The modal's poll runs while this is true, so its last read
 * carries the titled renderUrl and a failed reel's move to draft. Always
 * `!!regenerating` on a non-scene ad.
 */
export function sceneReelBusy(ad: { regenerating?: boolean | null; renderRoute?: string | null; sceneReel?: unknown }): boolean {
  if (ad.regenerating) return true;
  return retitleOnlyPending(ad);
}

/**
 * Only the retitle is left: a scene reel whose press has settled
 * (`regenerating` false) with its retitle still pending. Never true on a
 * non-scene ad.
 */
export function retitleOnlyPending(ad: { regenerating?: boolean | null; renderRoute?: string | null; sceneReel?: unknown }): boolean {
  if (ad.regenerating || !isSceneReelAd(ad)) return false;
  return toSceneReelSummary(ad.sceneReel)?.retitlePending === true;
}

/**
 * How long the modal's 5 s poll follows a pending retitle before it stops
 * and the strip says to refresh later. A retitle is normally minutes; one
 * that never settles (titler down, a stuck claim) must not poll for as long
 * as the modal stays open. Counted from when this follow first saw only the
 * retitle left; the regenerate itself (`regenerating`) is followed unbounded,
 * as on every other ad.
 */
export const RETITLE_FOLLOW_MAX_MS = 10 * 60_000;

/**
 * What a failed reel needs next (contract §6). A scene that fails for good
 * holds the scenes still mid-take, so a failed reel is completed one press
 * per scene without a usable take: `failedScene` names the next one (the
 * backend moves it after each press), `held` the others that will need their
 * own press, and the press that fills the last gap re-cuts the reel. null
 * when no scene has failed.
 */
export function failedReelGuidance(
  failed: { index: number } | null | undefined,
  scenes: ReadonlyArray<Pick<SceneReelSceneSummary, 'index' | 'status'>>
): { next: number; held: number[] } | null {
  if (!failed) return null;
  const held = scenes
    .filter(s => s.index !== failed.index && s.status !== 'ready')
    .map(s => s.index)
    .sort((a, b) => a - b);
  return { next: failed.index, held };
}

/** "Scene 2", "Scenes 1 and 2", "Scenes 1, 2 and 4" — 0-based indexes in, 1-based out. */
export function sceneListLabel(indexes: readonly number[]): string {
  const n = indexes.map(i => String(i + 1));
  if (n.length <= 1) return `Scene ${n[0] ?? ''}`.trim();
  return `Scenes ${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
}

/** "Scene 2 · lifestyle" / "Scene 4 · product shot". */
export function sceneTitle(scene: { index: number; kind: SceneReelSceneKind }): string {
  return `Scene ${scene.index + 1} · ${scene.kind === 'closing' ? 'product shot' : 'lifestyle'}`;
}

/** Seconds of the cut a scene occupies, e.g. "3 s". */
export function sceneDurationLabel(summary: Pick<SceneReelSummary, 'cut'>, index: number): string | null {
  const sec = summary.cut[index];
  return typeof sec === 'number' ? `${sec} s` : null;
}

/** "3 + 3 + 2 + 2 s". */
export function cutLabel(cut: readonly number[]): string {
  return `${cut.join(' + ')} s`;
}

/** Tooltip / one-line description for the Scene reel identity badge. */
export function sceneReelBadgeTitle(summary: SceneReelSummary | null | undefined): string {
  const cut = cutLabel(summary?.cut ?? SCENE_REEL_DEFAULT_CUT);
  const base = summary?.role === 'derive'
    ? `Scene reel — 1:1 cropped from the 9:16 version (${cut}). Edit scenes on the 9:16.`
    : `Scene reel — 10 s cut from 4 generated scenes (${cut}).`;
  const failed = summary?.failedScene;
  if (!failed) return base;
  return `${base} Scene ${failed.index + 1} failed${failed.reason ? `: ${failed.reason}` : ''}.`;
}

// ── Scene detail: GET /api/ads/:id/scenes?brandId= (API contract §5) ─────

export type SceneTakeTrigger = 'initial' | 'qc_retry' | 'replan' | 'operator_regenerate';
export type SceneTakeStatus = 'selected' | 'ready' | 'rejected' | 'failed' | 'open';

export type SceneTake = {
  id:                  string;
  takeNo:              number;
  trigger:             SceneTakeTrigger | null;
  status:              SceneTakeStatus | null;
  firstFrameUrl:       string | null;
  firstFrameGenerated: boolean;
  lastFrameUrl:        string | null;
  clipUrl:             string | null;
  clipThumbUrl:        string | null;
  prompt:              string | null;
  model:               string | null;
  qc:                  { grade: SceneReelQcGrade | null; findings: string[] };
  trim:                { startFrame: number; frames: number } | null;
  costUsd:             number | null;
  costSource:          'settled' | 'estimated' | null;
  createdAt:           string | null;
};

export type SceneSourceMode = 'photo' | 'convert' | 'persona' | 'product_only' | 'person';

export type SceneDetail = {
  index:          number;
  kind:           SceneReelSceneKind;
  status:         SceneReelSceneStatus;
  source:         { mode: SceneSourceMode | null; mediaId: string | null; url: string | null } | null;
  movement:       string | null;
  selectedTakeId: string | null;
  takes:          SceneTake[];
};

export type SceneReelDetail = {
  adId:        string;
  aspect:      string | null;
  model:       string | null;
  status:      string | null;
  failedScene: { index: number; reason: string | null } | null;
  /** The 9:16 a 1:1 is cropped from (the backend adds it on a derive only). */
  masterAdId:  string | null;
  scenes:      SceneDetail[];
  regenerate: {
    allowed:        boolean;
    reason:         string | null;
    /** The refusal's code (backend extra; e.g. SCENE_REEL_IN_FLIGHT), null when allowed. */
    code:           string | null;
    remainingToday: number | null;
    perScene:       Array<{ index: number; remainingToday: number | null }>;
  };
};

const TAKE_TRIGGERS: readonly SceneTakeTrigger[] = ['initial', 'qc_retry', 'replan', 'operator_regenerate'];
const TAKE_STATUSES: readonly SceneTakeStatus[] = ['selected', 'ready', 'rejected', 'failed', 'open'];
const SOURCE_MODES: readonly SceneSourceMode[] = ['photo', 'convert', 'persona', 'product_only', 'person'];

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function toTake(raw: unknown, i: number): SceneTake | null {
  if (!raw || typeof raw !== 'object') return null;
  const t = raw as Record<string, unknown>;
  const qc = t.qc && typeof t.qc === 'object' ? t.qc as Record<string, unknown> : {};
  const trim = t.trim && typeof t.trim === 'object' ? t.trim as Record<string, unknown> : null;
  return {
    id:                  str(t.id) ?? `take-${i}`,
    takeNo:              int(t.takeNo) ?? i + 1,
    trigger:             TAKE_TRIGGERS.includes(t.trigger as SceneTakeTrigger) ? t.trigger as SceneTakeTrigger : null,
    status:              TAKE_STATUSES.includes(t.status as SceneTakeStatus) ? t.status as SceneTakeStatus : null,
    firstFrameUrl:       str(t.firstFrameUrl),
    firstFrameGenerated: t.firstFrameGenerated === true,
    lastFrameUrl:        str(t.lastFrameUrl),
    clipUrl:             str(t.clipUrl),
    clipThumbUrl:        str(t.clipThumbUrl),
    prompt:              str(t.prompt),
    model:               str(t.model),
    qc: {
      grade:    QC_GRADES.includes(qc.grade as SceneReelQcGrade) ? qc.grade as SceneReelQcGrade : null,
      findings: Array.isArray(qc.findings) ? qc.findings.filter((f): f is string => typeof f === 'string' && !!f) : []
    },
    trim: trim && int(trim.startFrame) != null && int(trim.frames) != null
      ? { startFrame: int(trim.startFrame)!, frames: int(trim.frames)! }
      : null,
    costUsd:    num(t.costUsd),
    costSource: t.costSource === 'settled' || t.costSource === 'estimated' ? t.costSource : null,
    createdAt:  str(t.createdAt)
  };
}

function toSceneDetail(raw: unknown, i: number): SceneDetail | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  const src = s.source && typeof s.source === 'object' ? s.source as Record<string, unknown> : null;
  return {
    index:          int(s.index) ?? i,
    kind:           s.kind === 'closing' ? 'closing' : 'lifestyle',
    status:         SCENE_STATUSES.includes(s.status as SceneReelSceneStatus) ? s.status as SceneReelSceneStatus : 'pending',
    source: src ? {
      mode:    SOURCE_MODES.includes(src.mode as SceneSourceMode) ? src.mode as SceneSourceMode : null,
      mediaId: str(src.mediaId),
      url:     str(src.url)
    } : null,
    movement:       str(s.movement),
    selectedTakeId: str(s.selectedTakeId),
    takes: Array.isArray(s.takes)
      ? s.takes.map(toTake).filter((t): t is SceneTake => !!t).sort((a, b) => a.takeNo - b.takeNo)
      : []
  };
}

/** Normalise GET /api/ads/:id/scenes. null when the body is not a scene detail. */
export function toSceneReelDetail(raw: unknown): SceneReelDetail | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.scenes)) return null;
  const failedRaw = r.failedScene && typeof r.failedScene === 'object'
    ? r.failedScene as Record<string, unknown>
    : null;
  const failedIndex = failedRaw ? int(failedRaw.index) : null;
  const regen = r.regenerate && typeof r.regenerate === 'object' ? r.regenerate as Record<string, unknown> : {};
  return {
    adId:   str(r.adId) ?? '',
    aspect: str(r.aspect),
    model:  str(r.model),
    status: str(r.status),
    failedScene: failedRaw && failedIndex != null ? { index: failedIndex, reason: str(failedRaw.reason) } : null,
    masterAdId: str(r.masterAdId),
    scenes: r.scenes.map(toSceneDetail).filter((s): s is SceneDetail => !!s).sort((a, b) => a.index - b.index),
    regenerate: {
      // Fail closed: only an explicit true lets the operator spend.
      allowed:        regen.allowed === true,
      reason:         str(regen.reason),
      code:           str(regen.code),
      remainingToday: int(regen.remainingToday),
      perScene: Array.isArray(regen.perScene)
        ? regen.perScene
            .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
            .map(p => ({ index: int(p.index) ?? -1, remainingToday: int(p.remainingToday) }))
            .filter(p => p.index >= 0)
        : []
    }
  };
}

/** The take a scene is cut from: selectedTakeId, else the latest 'selected', else the latest take. */
export function selectedTake(scene: SceneDetail): SceneTake | null {
  return scene.takes.find(t => t.id === scene.selectedTakeId)
    || [...scene.takes].reverse().find(t => t.status === 'selected')
    || scene.takes[scene.takes.length - 1]
    || null;
}

export function takeTriggerLabel(trigger: SceneTakeTrigger | null): string {
  switch (trigger) {
    case 'initial':             return 'First take';
    case 'qc_retry':            return 'QC retry';
    case 'replan':              return 'Re-planned';
    case 'operator_regenerate': return 'Regenerated by you';
    default:                    return 'Take';
  }
}

export function qcGradeLabel(grade: SceneReelQcGrade | null): string {
  return grade === 'pass' ? 'QC pass' : grade === 'minor' ? 'QC minor' : grade === 'fail' ? 'QC fail' : 'Not checked';
}

/** "$0.31 settled" / "≈ $0.32 estimated"; null when no cost is recorded. */
export function takeCostLabel(take: Pick<SceneTake, 'costUsd' | 'costSource'>): string | null {
  if (take.costUsd == null) return null;
  const amount = `$${take.costUsd.toFixed(2)}`;
  return take.costSource === 'settled' ? `${amount} settled` : `≈ ${amount} estimated`;
}

// ── Scene regenerate: POST /api/ads/:id/scenes/:sceneIndex/regenerate (§6) ──

export type SceneRegenerateResponse = {
  adId:              string;
  sceneIndex:        number;
  regenerating:      boolean;
  regenerationStage: string | null;
  /** What the press will buy, as the backend counted it. Read through toSceneRegenerateBillable. */
  billable:          unknown;
  cascade:           string[];
};

/**
 * The 202's `billable` (contract §6): upper counts for the press's first
 * attempt — clips, the frame stills it cannot reuse, and, when the backend
 * reports them, vision judge calls (`judges`, null when not reported).
 */
export type SceneRegenerateBillable = { clips: number; stills: number; judges: number | null };

/**
 * Normalise the 202's `billable`. null when it is absent or any count is not
 * a whole number ≥ 0: the copy then names no counts rather than inventing
 * them (an older backend, or a shape change).
 */
export function toSceneRegenerateBillable(raw: unknown): SceneRegenerateBillable | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const b = raw as Record<string, unknown>;
  const clips = int(b.clips);
  const stills = int(b.stills);
  if (clips == null || stills == null) return null;
  if (b.judges === undefined || b.judges === null) return { clips, stills, judges: null };
  const judges = int(b.judges);
  return judges == null ? null : { clips, stills, judges };
}

function countLabel(n: number, one: string): string {
  return `${n} ${n === 1 ? one : `${one}s`}`;
}

/**
 * What a press bills, from the 202's counts with zeros left out: "up to 1
 * scene clip + 2 stills + 1 vision check", or "nothing". Read after "bills".
 * "Up to": the counts cover the first attempt, and a frame re-made from a
 * source photo is free.
 */
export function sceneRegenerateBillableText(b: SceneRegenerateBillable): string {
  const parts = [
    b.clips > 0 ? countLabel(b.clips, 'scene clip') : null,
    b.stills > 0 ? countLabel(b.stills, 'still') : null,
    b.judges ? countLabel(b.judges, 'vision check') : null
  ].filter((p): p is string => !!p);
  return parts.length ? `up to ${parts.join(' + ')}` : 'nothing';
}

export const SCENE_REGENERATE_PROMPT_MAX = 1000;

export function sceneRegenerateUrl(adId: string, sceneIndex: number, brandId: string): string {
  return `/api/ads/${encodeURIComponent(adId)}/scenes/${encodeURIComponent(String(sceneIndex))}/regenerate`
    + `?brandId=${encodeURIComponent(brandId)}`;
}

/** Body for a scene regenerate. The refinement is optional; frames default to kept. */
export function sceneRegenerateBody(prompt: string, regenerateFrames: boolean): { prompt?: string; regenerateFrames: boolean } {
  const trimmed = prompt.trim().slice(0, SCENE_REGENERATE_PROMPT_MAX);
  return { ...(trimmed ? { prompt: trimmed } : {}), regenerateFrames };
}
